import { supabase } from '../lib/supabase';
import openai from '../lib/openai';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const quizDifficultyService = {
  /**
   * Get user's quiz performance analytics
   */
  async getPerformanceAnalytics(userId, quizId) {
    try {
      const { data, error } = await supabase?.from('quiz_performance_analytics')?.select('*')?.eq('user_id', userId)?.eq('quiz_id', quizId)?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      
      return data;
    } catch (err) {
      console.error('Error fetching performance analytics:', err);
      throw err;
    }
  },

  /**
   * Analyze quiz attempt with AI for difficulty adjustment
   */
  async analyzeQuizAttempt(userId, quizId, attemptData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const analysisPrompt = `
Analyze this student's quiz performance and recommend the appropriate difficulty level for their next quiz:

Quiz Performance Data:
- Score: ${attemptData?.score}/${attemptData?.totalQuestions} (${Math.round(attemptData?.score / attemptData?.totalQuestions * 100)}%)
- Time Taken: ${Math.round(attemptData?.timeTaken / 60)} minutes
- Average Time Per Question: ${Math.round(attemptData?.timeTaken / attemptData?.totalQuestions)} seconds
- Questions Answered: ${JSON.stringify(attemptData?.answers)}
- Time Per Question: ${JSON.stringify(attemptData?.timePerQuestion)}

Historical Performance:
${attemptData?.previousAttempts ? `- Previous Attempts: ${attemptData?.previousAttempts}
- Average Score: ${attemptData?.averageScore}%
- Performance Trend: ${attemptData?.performanceTrend}` : '- First Attempt'}

Analyze:
1. Student's comprehension level based on score and response patterns
2. Time management skills (did they rush or take too long?)
3. Areas of strength and weakness
4. Recommended difficulty level (easy/medium/hard) for next quiz
5. Specific areas to focus on for improvement

Provide your analysis in the following JSON format:
{
  "comprehensionLevel": "string",
  "timeManagementAssessment": "string",
  "strengthAreas": ["array of strings"],
  "weaknessAreas": ["array of strings"],
  "recommendedDifficulty": "easy|medium|hard",
  "confidenceScore": 0.0-1.0,
  "improvementSuggestions": ["array of specific suggestions"],
  "performancePatterns": {
    "consistencyScore": 0.0-1.0,
    "progressionRate": "string",
    "learningStyle": "string"
  }
}
`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          { 
            role: 'system', content: 'You are an expert educational psychologist analyzing student performance to provide personalized learning recommendations. Provide detailed, actionable insights based on quiz performance data.' 
          },
          { role: 'user', content: analysisPrompt }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'quiz_analysis_response',
            schema: {
              type: 'object',
              properties: {
                comprehensionLevel: { type: 'string' },
                timeManagementAssessment: { type: 'string' },
                strengthAreas: { 
                  type: 'array', 
                  items: { type: 'string' } 
                },
                weaknessAreas: { 
                  type: 'array', 
                  items: { type: 'string' } 
                },
                recommendedDifficulty: { 
                  type: 'string',
                  enum: ['easy', 'medium', 'hard']
                },
                confidenceScore: { type: 'number' },
                improvementSuggestions: { 
                  type: 'array', 
                  items: { type: 'string' } 
                },
                performancePatterns: {
                  type: 'object',
                  properties: {
                    consistencyScore: { type: 'number' },
                    progressionRate: { type: 'string' },
                    learningStyle: { type: 'string' }
                  },
                  required: ['consistencyScore', 'progressionRate', 'learningStyle']
                }
              },
              required: ['comprehensionLevel', 'timeManagementAssessment', 'strengthAreas', 'weaknessAreas', 'recommendedDifficulty', 'confidenceScore', 'improvementSuggestions', 'performancePatterns'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',verbosity: 'high'
      });

      const analysis = JSON.parse(response?.choices?.[0]?.message?.content);

      const { data: analysisRecord, error: insertError } = await supabase?.from('quiz_difficulty_analysis')?.insert({
          user_id: userId,
          quiz_id: quizId,
          attempt_id: attemptData?.attemptId,
          ai_analysis: analysis,
          performance_patterns: analysis?.performancePatterns,
          difficulty_recommendation: analysis?.recommendedDifficulty,
          confidence_score: analysis?.confidenceScore,
          factors_analyzed: {
            score: attemptData?.score,
            totalQuestions: attemptData?.totalQuestions,
            timeTaken: attemptData?.timeTaken,
            previousAttempts: attemptData?.previousAttempts || 0
          }
        })?.select()?.single();

      if (insertError) throw insertError;

      return {
        analysis,
        analysisId: analysisRecord?.id
      };
    } catch (err) {
      const errorInfo = getErrorMessage(err);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing quiz attempt:', err);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Get adaptive questions based on user's performance level
   */
  async getAdaptiveQuestions(userId, quizId, requestedDifficulty) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let difficulty = requestedDifficulty;
      
      if (!difficulty) {
        const analytics = await this.getPerformanceAnalytics(userId, quizId);
        difficulty = analytics?.recommended_difficulty || 'medium';
      }

      const { data: questions, error } = await supabase?.from('quiz_questions')?.select('*')?.eq('quiz_id', quizId)?.eq('difficulty_level', difficulty)?.order('order_index');

      if (error) throw error;

      if (!questions || questions?.length === 0) {
        const { data: fallbackQuestions, error: fallbackError } = await supabase?.from('quiz_questions')?.select('*')?.eq('quiz_id', quizId)?.order('order_index');

        if (fallbackError) throw fallbackError;
        return fallbackQuestions || [];
      }

      return questions;
    } catch (err) {
      console.error('Error fetching adaptive questions:', err);
      throw err;
    }
  },

  /**
   * Update question performance metrics
   */
  async updateQuestionMetrics(questionId, quizId, wasCorrect, timeSpent) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: existing } = await supabase?.from('adaptive_question_pool')?.select('*')?.eq('question_id', questionId)?.eq('quiz_id', quizId)?.single();

      if (existing) {
        const newUsageCount = existing?.usage_count + 1;
        const currentSuccessRate = existing?.success_rate || 0;
        const newSuccessRate = ((currentSuccessRate * existing?.usage_count) + (wasCorrect ? 100 : 0)) / newUsageCount;
        
        const currentAvgTime = existing?.average_time_seconds || 0;
        const newAvgTime = ((currentAvgTime * existing?.usage_count) + timeSpent) / newUsageCount;

        await supabase?.from('adaptive_question_pool')?.update({
            success_rate: newSuccessRate,
            average_time_seconds: Math.round(newAvgTime),
            usage_count: newUsageCount,
            last_used: new Date()?.toISOString()
          })?.eq('id', existing?.id);
      } else {
        const { data: question } = await supabase?.from('quiz_questions')?.select('difficulty_level')?.eq('id', questionId)?.single();

        await supabase?.from('adaptive_question_pool')?.insert({
            quiz_id: quizId,
            question_id: questionId,
            difficulty_level: question?.difficulty_level || 'medium',
            success_rate: wasCorrect ? 100 : 0,
            average_time_seconds: timeSpent,
            usage_count: 1,
            last_used: new Date()?.toISOString()
          });
      }
    } catch (err) {
      console.error('Error updating question metrics:', err);
    }
  },

  /**
   * Get personalized quiz recommendations
   */
  async getQuizRecommendations(userId, playId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: analytics, error } = await supabase?.from('quiz_performance_analytics')?.select(`
          *,
          quizzes (
            id,
            title,
            description,
            play_id,
            difficulty_level
          )
        `)?.eq('user_id', userId)?.eq('play_id', playId)?.order('last_attempt_date', { ascending: false });

      if (error) throw error;

      return analytics || [];
    } catch (err) {
      console.error('Error fetching quiz recommendations:', err);
      throw err;
    }
  }
};